// ** UI
import Box from '@mui/material/Box';

// ** Components
import ComponentBlocksSearchFilter from '../strapi/Shared/ComponentBlocksSearchFilter';

// ** Types
import type React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SearchOption } from '@/components/common/searchSection/types';

export type FilterCardsGridSelectConfig = {
	id?: string;
	value: string;
	searchValue?: string;
	placeholder: string;
	options: SearchOption[];
	onChange: (value: string) => void;
	onSearchChange?: (value: string) => void;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	showCategories?: boolean;
	showSearch?: boolean;
};

export type FilterCardsGridProps = {
	children: React.ReactNode;
	listLabel?: string;
	itemWidthPx?: number;
	desktopColumns?: number;
	gapPx?: number;
	showFilter?: boolean;
	filterSelect?: FilterCardsGridSelectConfig;
	containerSx?: SxProps<Theme>;
	gridSx?: SxProps<Theme>;
};

const mergeSx = (sx?: SxProps<Theme>): SxProps<Theme>[] => (Array.isArray(sx) ? sx : sx ? [sx] : []);

const FilterBar = ({ config }: { config: FilterCardsGridSelectConfig }) => (
	<Box sx={{ width: '100%', mb: '48px' }}>
		<ComponentBlocksSearchFilter
			config={{
				searchFilterShowCategory: config.showCategories,
				searchFilterShowSearch: config.showSearch,
				searchFilterShowPeriod: false,
				searchFilterCategoryPlaceholder: config.placeholder || 'Kategorie',
				searchFilterSearchPlaceholder: 'Suchen...'
			}}
			state={{
				selectedCategory: config.value,
				searchTerm: config.searchValue || ''
			}}
			handlers={{
				onCategoryChange: (val: string | null) => config.onChange?.(val ?? ''),
				onSearchChange: (val: string) => config.onSearchChange?.(val),
				onPeriodChange: () => {
					/* No-op */
				},
				onSearchSubmit: () => {
					/* No-op */
				}
			}}
			options={{
				categories: config.options,
				periods: []
			}}
			backgroundColor="#F6F4F2"
		/>
	</Box>
);

const FilterCardsGrid = ({
	children,
	listLabel = 'Cards list',
	itemWidthPx = 308,
	desktopColumns = 4,
	gapPx = 16,
	showFilter,
	filterSelect,
	containerSx,
	gridSx
}: FilterCardsGridProps) => {
	const layout = {
		twoColWidth: itemWidthPx * 2 + gapPx,
		threeColWidth: itemWidthPx * 3 + gapPx * 2,
		desktopWidth: itemWidthPx * desktopColumns + gapPx * (desktopColumns - 1),
		bp990: '@media (min-width:990px)',
		bp1440: '@media (min-width:1440px)'
	};

	const isFilterVisible = filterSelect !== null && (showFilter ?? true);

	return (
		<Box
			sx={
				[
					{
						width: '100%'
					},
					...mergeSx(containerSx)
				] as SxProps<Theme>
			}
		>
			<Box
				sx={{
					width: '100%',
					maxWidth: {
						xs: '100%',
						sm: `${layout.twoColWidth}px`
					},
					[layout.bp990]: {
						maxWidth: `${layout.threeColWidth}px`
					},
					[layout.bp1440]: {
						maxWidth: `${layout.desktopWidth}px`
					},
					mx: 'auto'
				}}
			>
				{isFilterVisible && filterSelect && <FilterBar config={filterSelect} />}

				<Box
					component="ul"
					aria-label={listLabel}
					sx={
						[
							{
								display: 'grid',
								gridTemplateColumns: {
									xs: 'minmax(0, 1fr)',
									sm: 'repeat(2, minmax(0, 1fr))'
								},
								[layout.bp990]: {
									gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
								},
								[layout.bp1440]: {
									gridTemplateColumns: `repeat(${desktopColumns}, minmax(0, 1fr))`
								},
								width: '100%',
								gap: `${gapPx}px`,
								listStyle: 'none',
								m: 0,
								p: 0
							},
							...mergeSx(gridSx)
						] as SxProps<Theme>
					}
				>
					{children}
				</Box>
			</Box>
		</Box>
	);
};

export default FilterCardsGrid;
